import { Button, ButtonGroup, Stack, Text } from '@chakra-ui/react';
import { TimeOfDay } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { timeOfDayOptions } from '@pelicargo/utils';
import { addDays, isSameDay, isValid, parseISO } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { FieldPathByValue, PathValue, useFormContext, useWatch } from 'react-hook-form';

import { SimpleXProps } from '../SimpleField';
import { DayType } from './Calendar/type';
import { DateTimePicker } from './DateTimePicker';

// The picker stores the picked calendar day as UTC midnight, so anything
// comparing against its value (min dates, zod floors) has to use the same shape
// rather than a local-midnight date.
export const getTodayUtc = () => {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
};

export type Props<FormSchema> = SimpleXProps<FormSchema> & {
  name: FieldPathByValue<FormSchema, string | Date>;
  variant?: string;
  matchWidth?: boolean;
  timeOfDay?: boolean;
  timeOfDayName?: FieldPathByValue<FormSchema, TimeOfDay>;
  iconFontSize?: string; // Remove this?? It's unused
  placeholder?: string;
  fontSize?: string;
  /** Earliest selectable day, as a UTC-midnight date. Defaults to yesterday. */
  minDate?: Date;
  onChange?: (value: string) => void;
  disablePastDates?: boolean;
};

export const SimpleDatePicker = <FormSchema extends {}>({
  name,
  timeOfDay = false,
  timeOfDayName,
  onChange,
  disablePastDates = false,
  ...rest
}: Props<FormSchema>) => {
  const { setValue } = useFormContext<FormSchema>();
  const formValue = useWatch({ name });
  const timeOfDayValue = useWatch({ name: timeOfDayName ?? '' });

  const todayUtc = useMemo(
    () => new Date(Date.UTC(new Date().getFullYear(), new Date().getMonth(), new Date().getDate())),
    [],
  );

  const selectedDate = useMemo(() => {
    if (!formValue) return todayUtc;

    const parsed = parseISO(formValue);
    if (!isValid(parsed)) return todayUtc;

    const utcParsed = new Date(Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()));
    if (disablePastDates && utcParsed < todayUtc) return todayUtc;

    return parsed;
  }, [disablePastDates, formValue, todayUtc]);

  const handleSetDate = useCallback(
    (nextDate: Date) => {
      const utcDate = new Date(Date.UTC(nextDate.getFullYear(), nextDate.getMonth(), nextDate.getDate()));
      setValue(
        name,
        utcDate.toISOString() as PathValue<FormSchema, keyof FormSchema & FieldPathByValue<FormSchema, string | Date>>,
      );
      onChange?.(utcDate.toISOString());
    },
    [setValue, name, onChange],
  );

  const onDayTypeRulesFn = useCallback(
    (date: Date) => {
      const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));

      if (disablePastDates) {
        if (utcDate < todayUtc) return DayType.DISABLE;
      } else if (utcDate < addDays(todayUtc, -1)) {
        return DayType.DISABLE;
      }

      const formValueDate = formValue ? parseISO(formValue) : null;
      const hasValueAndIsSame = formValueDate && isSameDay(utcDate, formValueDate);
      if (hasValueAndIsSame) return DayType.ACTIVE;

      return DayType.NORMAL;
    },
    [disablePastDates, formValue, todayUtc],
  );

  return (
    <Stack w="full" flexDir="column" {...rest}>
      <Show if={timeOfDay}>
        <Text textStyle="body">Tender date</Text>
      </Show>
      <DateTimePicker
        selectedDate={selectedDate}
        onSetDate={handleSetDate}
        onDayTypeRulesFn={onDayTypeRulesFn}
        minDate={disablePastDates ? todayUtc : undefined}
      />
      <Show if={timeOfDay}>
        <Stack spacing="2">
          <Text textStyle="body">Time</Text>
          <ButtonGroup w="full">
            {timeOfDayOptions.map((option) => (
              <Button
                key={option.value}
                value={option.value}
                size="xs"
                w="full"
                bg={option.value === timeOfDayValue ? 'gray.800' : 'gray.100'}
                color={option.value === timeOfDayValue ? 'white' : 'gray.800'}
                _hover={{
                  bg: option.value === timeOfDayValue ? 'gray.800' : 'gray.200',
                }}
                onClick={() =>
                  setValue(
                    timeOfDayName,
                    option.value as PathValue<FormSchema, FieldPathByValue<FormSchema, TimeOfDay>>,
                  )
                }
              >
                {option.label}
              </Button>
            ))}
          </ButtonGroup>
        </Stack>
      </Show>
    </Stack>
  );
};
